import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, map, startWith, Subject } from 'rxjs';

import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
  type OidcClientNotification,
} from 'angular-auth-oidc-client';

import {
  AccessTokenProvider,
  AccessTokenUnavailableError,
} from '../realtime/access-token.provider';
import type { IssuerCategory } from './claims-mapping';
import { OidcSessionService } from './oidc-session.service';

/**
 * The real AccessTokenProvider (R-106, contracts/access-token-provider.contract.md):
 * returns the active session configuration's API access token from the OIDC
 * library's in-memory state. The port signature is the frozen AITH-31 seam;
 * this class is the single token egress — no interceptor, no second path.
 *
 * Fail-closed: rejects with AccessTokenUnavailableError when no authenticated
 * session exists, the session is not provisioned, the library cannot produce
 * a token, or the AbortSignal fires first. Token material never appears in
 * errors, logs, or storage beyond the library's in-memory state (FR-015).
 */
@Injectable({ providedIn: 'root' })
export class OidcAccessTokenProvider extends AccessTokenProvider {
  private readonly oidc = inject(OidcSecurityService);
  private readonly session = inject(OidcSessionService);
  private readonly oidcEvents = inject(PublicEventsService);

  /**
   * The owner whose silent renewal is currently in flight (contract §3).
   * `SilentRenewStarted` carries no payload in the pinned library, so the
   * owner is captured from the session at that moment; the epoch is kept so a
   * marker left behind by a previous owner is not followed by a replacement
   * session that happens to reach the same issuer.
   */
  private renewalInFlight: { category: IssuerCategory; epoch: number } | null = null;
  private readonly renewalSettled = new Subject<{ category: IssuerCategory; ok: boolean }>();

  /**
   * Ownership state as an observable (startWith reads the current value on
   * subscribe, so a derivation that lands before the waiter subscribes is
   * never missed). Used to hold a followed renewal until its validated
   * claims are applied (renewalRevision advances) or the owner changes
   * (sessionEpoch advances).
   */
  private readonly ownership$ = this.session.ownershipChanges$.pipe(
    startWith(void 0),
    map(() => ({
      epoch: this.session.sessionEpoch(),
      renewals: this.session.renewalRevision(),
    })),
  );

  constructor() {
    super();
    this.oidcEvents
      .registerForEvents()
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((notification) => this.trackRenewal(notification));
  }

  override async getAccessToken(signal: AbortSignal): Promise<string> {
    this.throwIfAborted(signal);
    const category = this.session.issuerCategory();
    if (category === null || !this.session.isAuthenticated()) {
      throw new AccessTokenUnavailableError();
    }
    // Ownership across the awaits below is tracked by the session epoch, not
    // by category alone: a sign-out plus a new sign-in through the same
    // issuer advances the epoch and is rejected, while a correlated silent
    // renewal deliberately does not advance it — the renewal-following path
    // (contract §3) keeps serving the same owner even though the renewal
    // derivation replaces the Session object itself.
    const owningEpoch = this.session.sessionEpoch();
    const renewalsSeen = this.session.renewalRevision();

    // Follow only a renewal started by this very session: a stale marker from
    // a previous owner would otherwise make this call wait for a renewal whose
    // derivation is now rejected, and nothing would ever release it.
    if (
      this.renewalInFlight?.category === category
      && this.renewalInFlight.epoch === owningEpoch
    ) {
      // Contract §3: follow the renewal rather than returning a near-expiry
      // token — this keeps SC-004 at zero failed requests during renewal.
      const settled = await this.raceAbort(
        firstValueFrom(
          this.renewalSettled.pipe(filter((entry) => entry.category === category)),
        ),
        signal,
      );
      this.throwIfAborted(signal);
      if (!settled.ok) throw new AccessTokenUnavailableError();
      // The library-level result alone is not a usable renewal: the flow
      // still has to read and derive the renewed claims and may fail closed
      // (revoked role, reused iat, payload read failure). Hold until the
      // derivation lands (renewalRevision advances) or the owner changes
      // (sessionEpoch advances — teardown or replacement), then re-check.
      await this.raceAbort(
        firstValueFrom(
          this.ownership$.pipe(
            filter((state) => state.renewals !== renewalsSeen || state.epoch !== owningEpoch),
          ),
        ),
        signal,
      );
      this.throwIfAborted(signal);
      this.throwIfOwnerChanged(category, owningEpoch);
    }

    const token = await this.raceAbort(
      firstValueFrom(this.oidc.getAccessToken(category)),
      signal,
    );
    this.throwIfAborted(signal);
    this.throwIfOwnerChanged(category, owningEpoch);
    if (typeof token !== 'string' || token === '') {
      throw new AccessTokenUnavailableError();
    }
    return token;
  }

  /**
   * Fail closed when the session that started this call no longer owns the
   * issuer: ended, replaced (epoch advanced), or re-categorized during an
   * await. A renewal derivation keeps the epoch, so following a correlated
   * renewal never trips this.
   */
  private throwIfOwnerChanged(category: IssuerCategory, owningEpoch: number): void {
    if (
      this.session.sessionEpoch() !== owningEpoch
      || this.session.issuerCategory() !== category
      || !this.session.isAuthenticated()
    ) {
      throw new AccessTokenUnavailableError();
    }
  }

  /**
   * Tracks the library's silent-renew lifecycle for the active session's
   * configuration. Only one configuration can own the session at a time
   * (most-recent-session-wins), so a single in-flight marker suffices; a
   * renewal event without a config id is attributed to the active session.
   */
  private trackRenewal(notification: OidcClientNotification<unknown>): void {
    if (notification.type === EventTypes.SilentRenewStarted) {
      const category = notificationCategory(notification) ?? this.session.issuerCategory();
      this.renewalInFlight = category === null
        ? null
        : { category, epoch: this.session.sessionEpoch() };
      return;
    }
    if (
      notification.type === EventTypes.NewAuthenticationResult ||
      notification.type === EventTypes.SilentRenewFailed
    ) {
      const inFlight = this.renewalInFlight?.category ?? null;
      if (inFlight === null) return;
      const completedCategory = notificationCategory(notification);
      if (completedCategory !== null && completedCategory !== inFlight) return;
      // A payload-free event is the library's real terminal-failure shape, and
      // AuthFlowService — which subscribes first, during APP_INITIALIZER —
      // has already ended the session synchronously by the time this handler
      // runs. Consulting the live session would therefore discard every real
      // failure and leave a following call pending; the captured marker is the
      // sound attribution.
      //
      // A config-less *success* is unreachable in the pinned 19.0.2 — all five
      // `updateAndPublishAuthState` call sites carry `configId` — but it must
      // not be settled `ok` if that ever changes: `AuthFlowService`'s own
      // handler drops a config-less result (it cannot attribute it either), so
      // nothing would advance `renewalRevision` or `sessionEpoch` and the
      // derivation hold below would block until the caller's signal aborts.
      // An unattributable renewal is a failed renewal — settle it closed.
      if (
        completedCategory === null
        && notification.type === EventTypes.NewAuthenticationResult
      ) {
        this.renewalInFlight = null;
        this.renewalSettled.next({ category: inFlight, ok: false });
        return;
      }
      if (
        notification.type === EventTypes.NewAuthenticationResult
        && !isGenuineRenewalResult(notification.value)
      ) {
        // An initial (popup) authentication result for the same configuration
        // is not the tracked renewal — its own completion or failure event
        // settles it later. Settling `ok` here would release waiters on a
        // result AuthFlowService will never derive from, leaving them to
        // hang on the derivation hold above.
        return;
      }
      this.renewalInFlight = null;
      this.renewalSettled.next({
        category: inFlight,
        ok: notification.type === EventTypes.NewAuthenticationResult,
      });
    }
  }

  private throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted) throw new AccessTokenUnavailableError();
  }

  /**
   * Awaits a value, bounded by the AbortSignal; failures map to the port's
   * terminal-unavailable error so token material and provider internals never
   * leak (contract §2/§4).
   */
  private raceAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const onAbort = (): void => reject(new AccessTokenUnavailableError());
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(
        (value) => {
          signal.removeEventListener('abort', onAbort);
          resolve(value);
        },
        () => {
          signal.removeEventListener('abort', onAbort);
          reject(new AccessTokenUnavailableError());
        },
      );
    });
  }
}

/** Whether an authentication result is a completed, authenticated silent renewal. */
function isGenuineRenewalResult(value: unknown): boolean {
  return (
    typeof value === 'object'
    && value !== null
    && (value as Record<string, unknown>)['isRenewProcess'] === true
    && (value as Record<string, unknown>)['isAuthenticated'] === true
  );
}

function notificationCategory(
  notification: OidcClientNotification<unknown>,
): IssuerCategory | null {
  const value = notification.value;
  if (typeof value !== 'object' || value === null) return null;
  const configId = (value as Record<string, unknown>)['configId'];
  if (configId === 'staff') return 'staff';
  if (configId === 'client') return 'client';
  return null;
}
